import { HStack } from '@chakra-ui/layout';
import { Document as RichTextDocument } from '@contentful/rich-text-types';
import React from 'react';

import { WLLink } from '../../design-library';
import IconAdapter from '../IconAdapter';
import RichText, { RichTextAlignment } from '../RichText';

interface LinksArrayAdapterProps {
  json: string | null | undefined;
  arrow?: boolean;
  /**
   * Optional filtering callback to determine whether a given link should be excluded from the output
   * @param linkPath
   */
  exclude?: (linkPath: string) => boolean;
  isHeader?: boolean;
}

interface LinksArrayElementProps {
  iconName?: string | null | undefined;
  text: string;
  href: string | null | undefined;
  arrow: boolean | null | undefined;
  isHeader: boolean;
}

export type LinksArrayItem = {
  icon: { value: string };
  label: { value: RichTextDocument };
  target: { value: string };
};

const LinksArrayElement: React.FC<LinksArrayElementProps> = ({
  iconName,
  text,
  href,
  arrow,
  isHeader,
}) => {
  const styles = {
    p: {
      marginBlockEnd: 0,
      fontSize: '14px',
      lineHeight: '18px',
    },
    b: {
      fontSize: '1rem',
      lineHeight: '22px',
    },
    'h1, h2, h3, h4, h5, h6': {
      marginBlockEnd: '4px',
    },
  };
  const icon = iconName ? (
    <IconAdapter
      name={iconName}
      boxSize={isHeader ? '2em' : '4rem'}
      marginRight={arrow ? [2, 4] : undefined}
      variant={isHeader ? 'darkIconNoBackground' : undefined}
    />
  ) : null;
  const content = (
    <RichText
      alignment={
        isHeader
          ? undefined
          : arrow
          ? RichTextAlignment.LEFT
          : RichTextAlignment.CENTER
      }
      value={{
        raw: text,
        references: undefined as any,
      }}
    />
  );

  if (href) {
    return (
      <WLLink
        to={href}
        arrow={!!arrow}
        width={isHeader ? undefined : 'full'}
        variant="plain"
        flexDirection={isHeader || arrow ? 'row' : 'column'}
        sx={styles}
      >
        {icon}
        {content}
      </WLLink>
    );
  }
  return (
    <HStack sx={styles}>
      {icon}
      {content}
    </HStack>
  );
};

const LinksArrayAdapter: React.FC<LinksArrayAdapterProps> = ({
  json,
  arrow,
  exclude,
  isHeader = false,
}) => {
  const data = React.useMemo(() => json && JSON.parse(json), [json]);
  if (typeof data !== 'object' || !data.data) {
    return null;
  }
  const array = data.data as LinksArrayItem[];

  if (!array || array.length === 0) {
    return null;
  }

  return (
    <>
      {array
        .filter((item) => {
          if (!exclude) {
            return true;
          }
          return !exclude(item.target.value);
        })
        .map((item, index) => (
          <LinksArrayElement
            key={index}
            iconName={item.icon?.value}
            text={JSON.stringify(item.label?.value)}
            href={item.target?.value}
            arrow={arrow}
            isHeader={isHeader}
          />
        ))}
    </>
  );
};

export default LinksArrayAdapter;
